import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Bank, FloppyDisk } from "@phosphor-icons/react";

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const blankBank = { bank_name: "", account_name: "", account_no: "", ifsc: "", branch: "" };

export default function ProformaSettingsDialog({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bank, setBank] = useState(blankBank);
  const [signatory, setSignatory] = useState("");
  const [terms, setTerms] = useState([]);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/proforma/settings");
      setBank({ ...blankBank, ...(data.bank || {}) });
      setSignatory(data.signatory_name || "");
      setTerms(data.default_terms || []);
      setNotes(data.default_notes || "");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/proforma/settings", {
        bank,
        signatory_name: signatory,
        default_terms: terms,
        default_notes: notes,
      });
      toast.success("Proforma settings saved — new invoices will use these bank details");
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose?.(); }}>
      <DialogContent className="max-w-2xl" data-testid="pi-settings-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px] flex items-center gap-2">
            <Bank size={16} weight="fill" className="text-[#EA580C]" /> Proforma Invoice Settings
          </DialogTitle>
          <DialogDescription className="text-[12px] text-slate-500">
            These bank details and defaults appear on every new Proforma Invoice and on the PDF footer. Existing invoices keep their saved values.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-400 text-sm">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-[#EA580C] mb-2">Company Bank Details</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="Account Name"><Input value={bank.account_name} onChange={(e) => setBank({ ...bank, account_name: e.target.value })} className="h-8 text-[12px]" data-testid="pi-set-acname" /></Field>
                <Field label="Bank Name"><Input value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} className="h-8 text-[12px]" data-testid="pi-set-bankname" /></Field>
                <Field label="Account No."><Input value={bank.account_no} onChange={(e) => setBank({ ...bank, account_no: e.target.value })} className="h-8 text-[12px] font-mono" data-testid="pi-set-acno" /></Field>
                <Field label="IFSC"><Input value={bank.ifsc} onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase() })} className="h-8 text-[12px] font-mono" data-testid="pi-set-ifsc" /></Field>
                <Field label="Branch" className="col-span-2"><Input value={bank.branch} onChange={(e) => setBank({ ...bank, branch: e.target.value })} className="h-8 text-[12px]" data-testid="pi-set-branch" /></Field>
                <Field label="Authorised Signatory" className="col-span-2 md:col-span-3"><Input value={signatory} onChange={(e) => setSignatory(e.target.value)} placeholder="Name printed above the signature line" className="h-8 text-[12px]" data-testid="pi-set-signatory" /></Field>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <Field label="Default Terms & Conditions (one per line)">
                <Textarea value={(terms || []).join("\n")} onChange={(e) => setTerms(e.target.value.split("\n"))} rows={5} className="text-[11.5px]" data-testid="pi-set-terms" />
              </Field>
              <Field label="Default Notes">
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="text-[11.5px]" data-testid="pi-set-notes" />
              </Field>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} className="h-9 text-[12px]">Cancel</Button>
          <Button onClick={save} disabled={busy || loading} className="h-9 text-[12px] bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="pi-settings-save">
            <FloppyDisk size={14} className="mr-1.5" /> {busy ? "Saving…" : "Save settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
